<template>
	<md-card>
		<md-card-header class="md-card-header-icon md-card-header-blue">
			<div class="md-layout md-gutter">
				<div class="md-layout-item">
					<div class="card-icon">
						<font-awesome-icon class="md-icon" :icon="['fa', 'bars']" />
					</div>
					<h4 class="title">Liste des interventions mal planifiées</h4>
				</div>
			</div>
		</md-card-header>
		<md-card-content>
			<div class="flex items-center justify-between">
				<div class="flex items-center space-x-4 divide-x-2">
					<div class="max-w-min">
						<md-field class="md-invalid">
							<label>Année</label>
							<md-input type="number" v-model="year" @keydown.enter="getInterventions" />
						</md-field>
					</div>
					<md-button class="md-success md-icon-button md-raised my-auto" @click="getInterventions">
						<font-awesome-icon class="md-icon" :icon="['fa', 'filter']" />
						<span class="font-medium ml-2 leading-none">Filtrer</span>
					</md-button>
					<md-button class="md-default md-icon-button md-raised my-auto" @click="resetFilters">
						<font-awesome-icon class="md-icon" :icon="['fa', 'times-circle']" />
						<span class="font-medium ml-2 leading-none">Réinitialiser</span>
					</md-button>
					<span class="font-medium pl-2">{{ `Total : ${this.pagination.total} interventions` }}</span>
				</div>
				<div style="min-width: 200px;">
					<div class="md-multiselect">
						<label>Éléments à afficher</label>
						<multiselect v-model="pagination.perPage"
						             :options="pagination.perPageOptions"
						             :searchable="false"
						             :show-labels="false"
						             :allow-empty="false">
							<template slot="noOptions">La liste est vide</template>
							<template slot="noResult">Aucun élement correspondant</template>
						</multiselect>
					</div>
				</div>
			</div>
			<div class="relative" style="min-height: 150px;">
				<md-table class="paginated-table no-actions" :value="interventions" table-header-color="blue">
					<md-table-row v-if="interventions.length > 0" slot="md-table-row" slot-scope="{ item }" :class="{ 'row-success': item.is_poorly_planned_valid }">
						<md-table-cell md-label="# Intervention" class="cell-inter-info">
							<div class="poorly-indicator"></div>
							<div class="pl-2">
								<span class="font-medium mr-1 cursor-pointer hover:underline"
								      @mousedown="goToRoute($event, 'Intervention Details', { id: item.id })">
									{{ item.id }}
								</span>
							</div>
						</md-table-cell>
						<md-table-cell md-label="Site - Type" style="max-width: 800px; width: 800px;">
							<div class="font-medium">
								<span class="mr-1 cursor-pointer hover:underline" @mousedown="goToRoute($event, 'Site Details', { id: item.client_id, idSite: item.site_id })">{{ item.site_name }}</span>
								<small class="cursor-pointer hover:underline" @mousedown="goToRoute($event, 'Client Details', { id: item.client_id })">({{ item.client_name }})</small>
							</div>
							<div>{{ item.intervention_type }}</div>
						</md-table-cell>
						<md-table-cell md-label="Date">
							<span>{{ $moment(item.start_date).format('DD/MM/YYYY') }}</span>
						</md-table-cell>
						<md-table-cell md-label="Année PI">
							<span>{{ $moment(item.planification_date).format('YYYY') + ' (' + $moment(item.planification_date).format('YYYYMM') + ')' }}</span>
						</md-table-cell>
						<md-table-cell md-label="État">
							<div class="flex items-center justify-between">
								<span>{{ item.is_poorly_planned_valid ? 'Traitée' : 'Non traitée' }}</span>
								<div>
									<md-button class="md-icon-button" :class="item.is_poorly_planned_valid ? 'md-warning' : 'md-success'" @click="updateIntervention(item.id)">
										<font-awesome-icon class="md-icon" :icon="['fa', item.is_poorly_planned_valid ? 'undo': 'check']" />
									</md-button>
								</div>
							</div>
						</md-table-cell>
					</md-table-row>
					<md-table-empty-state v-if="!loading"
					                      md-label="Pas d'interventions trouvées"
					                      md-description="Essayez une recherche différente">
					</md-table-empty-state>
				</md-table>
				<div v-if="loading" class="loader-overlay opacity-100 absolute z-50 w-full h-full">
					<div class="h-full w-full flex items-center justify-center">
						<md-progress-spinner class="md-white" md-mode="indeterminate"
						                     :md-diameter="50" :md-stroke="4"></md-progress-spinner>
					</div>
				</div>
			</div>
		</md-card-content>
		<md-card-actions md-alignment="space-between">
			<p class="card-category">
				Affichage des éléments {{ from + 1 }} à {{ to }} sur {{ pagination.total }} éléments
			</p>
			<pagination
				class="pagination-no-border pagination-info"
				v-model="pagination.currentPage"
				:per-page="pagination.perPage"
				:total="pagination.total"
				@input="getInterventions"
			>
			</pagination>
		</md-card-actions>
	</md-card>
</template>

<script>
import { Pagination } from '../../../components'
import routerMixin from "../../../mixins/router-mixin";

export default {
	name: "InterventionsPoorlyPlanned",
	components: { Pagination },
	mixins: [routerMixin],
	props: {
		active: Boolean
	},
	data() {
		return {
			interventions: [],
			year: this.$moment(new Date()).year(),
			pagination: {
				perPage: 100,
				currentPage: 1,
				perPageOptions: [100, 200, 300],
				total: 0
			},
			loading: true
		}
	},
	computed: {
		to() {
			let highBound = this.from + this.pagination.perPage;
			if (this.pagination.total < highBound) {
				highBound = this.pagination.total;
			}
			return highBound;
		},
		from() {
			return this.pagination.perPage * (this.pagination.currentPage - 1);
		}
	},
	methods: {
		async getInterventions() {
			this.loading = true
			const { data } = await this.$store.dispatch('stats/getInterventionsPoorlyPlanned', {
				page: this.pagination.currentPage,
				perPage: this.pagination.perPage,
				year: this.year
			})
			this.interventions = data.data
			this.pagination.total = data.total
			this.loading = false
		},
		resetFilters() {
			this.year = this.$moment(new Date()).year()
			this.getInterventions()
		},
		async updateIntervention(interventionId) {
			this.loading = true
			try {
				await this.$store.dispatch('stats/updateInterventionPoorlyPlanned', {
					intervention_id: interventionId
				})
				// TODO: message différent si on passe à traité ou pas
				await this.$store.dispatch('alerts/success', 'Intervention traitée')
				await this.getInterventions()
			} catch (e) {}
		}
	},
	mounted() {
		if (this.active) {
			this.getInterventions()
		}
	},
	beforeDestroy() {
		this.interventions = []
	},
	watch: {
		active: function (newVal, oldVal) {
			if (newVal) {
				this.year = this.$moment(new Date()).year()
				this.getInterventions()
			}
		}
	}
}
</script>

<style scoped lang="scss">
::v-deep {
	.md-table {
		.md-table-row {
			.cell-inter-info {
				.md-table-cell-container {
					position: relative;
					height: 100%;
					display: flex;
					align-items: center;

					.poorly-indicator {
						position: absolute;
						top: 0;
						left: -5px;
						height: 100%;
						min-height: 37px;
						width: 5px;
						z-index: 100;
						border-radius: 2px;
					}
				}
			}

			&.row-success {
				.poorly-indicator {
					background: $brand-success;
				}
			}
		}
	}
}
</style>
